import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hrApi } from '@/api';import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import type { HrApprovalRequest } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';

export function HrApprovalsPage() {
  const [searchParams] = useSearchParams();
  const mineOnly = searchParams.get('mine') === '1';
  const pagination = usePagination();
  const [data, setData] = useState<HrApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(mineOnly ? 'PENDING' : 'PENDING');
  const [onlyMine, setOnlyMine] = useState(mineOnly);
  const [commentsById, setCommentsById] = useState<Record<number, string>>({});
  const [actingId, setActingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = onlyMine ? 'PENDING' : statusFilter === 'ALL' ? undefined : statusFilter;
      const rows = await hrApi.listApprovalRequests(status, onlyMine);
      setData(rows);
      pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onlyMine, pagination]);

  useEffect(() => {
    pagination.resetPage();
  }, [statusFilter, onlyMine, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, action: 'APPROVE' | 'REJECT') => {
    setActingId(id);
    try {
      const result = await hrApi.actApproval(id, action, commentsById[id] || undefined);
      notify.success(`Request ${result.statusCode.toLowerCase()}`);
      load();
    } catch (err) {
      showApiError(err, 'Approval action failed');
    } finally {
      setActingId(null);
    }
  };

  const columns: Column<HrApprovalRequest>[] = [
    { key: 'id', header: '#' },
    { key: 'workflowName', header: 'Workflow' },
    { key: 'referenceType', header: 'Type' },
    { key: 'referenceId', header: 'Ref' },
    {
      key: 'currentStageOrder',
      header: 'Stage',
      render: (row) => row.currentStageName ?? row.currentStageOrder,
    },
    { key: 'statusCode', header: 'Status' },
    {
      key: 'id',
      header: 'Actions',
      render: (row) =>
        row.statusCode === 'PENDING' && row.canAct !== false ? (
          <div className="flex min-w-[220px] flex-col gap-1">
            <Input
              placeholder="Comments"
              value={commentsById[row.id] ?? ''}
              onChange={(e) =>
                setCommentsById((prev) => ({ ...prev, [row.id]: e.target.value }))
              }
            />
            <div className="flex gap-1">
              <Button
                variant="secondary"
                loading={actingId === row.id}
                onClick={() => void act(row.id, 'REJECT')}
              >
                Reject
              </Button>
              <Button loading={actingId === row.id} onClick={() => void act(row.id, 'APPROVE')}>
                Approve
              </Button>
            </div>
          </div>
        ) : row.statusCode === 'PENDING' ? (
          <span className="text-sm text-slate-500">Awaiting other approver</span>
        ) : (
          <span className="text-sm text-slate-500">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Approvals"
        description="Central inbox for leave, expense, overtime, payroll, and exit approval workflows"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Approvals' }]}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="text-sm text-slate-600 dark:text-slate-300">Show</label>
        <Select
          className="min-w-[8rem]"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          disabled={onlyMine}
          options={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'ALL', label: 'All' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
          My queue only
        </label>
        <Button variant="secondary" onClick={() => load()}>
          Refresh
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={paginateClientList(data, pagination.page, pagination.size)}
        loading={loading}
        keyField="id"
        {...dataTablePageProps(pagination)}
      />
    </div>
  );
}
